// ─────────────────────────────────────────────────────────────────────────────
// financeSummary — single source of truth for a project's commercial position.
//
// Both the web Financial Dashboard and the mobile Financial tab compute their
// figures here so the two screens can never drift. The logic mirrors the
// Variation Order Register / SOQ Register conventions:
//   • Certificates are CUMULATIVE — certified-to-date is the LATEST cert's
//     subtotal A, NOT the sum of every cert.
//   • Tender baseline comes from the live SOQ register when populated, falling
//     back to the BOQ tendered total for legacy projects.
//   • Approved VO additions = additionalWorkAmount + existingRatesAmount.
//   • Omissions/savings are netted once (VO omissionItems + SOQ zero-qty items,
//     with no double-count between them).
// ─────────────────────────────────────────────────────────────────────────────
import { computeTenderedTotal, computeEngineersEstimateTotal } from "../components/SOQRegisterSection";

export function numberOrZero(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normItemNo(v) { return String(v || "").trim().toUpperCase(); }

export function isApprovedVariation(item) {
  return String(item?.status || "").trim().toLowerCase() === "approved";
}

export function computeFinanceSummary({
  pid,
  soqItems = [],
  boqRows = [],
  boqTenderedTotal = 0,
  variations = [],
  claims = [],
  certificates = [],
  engineersEstimateTotal = 0,
} = {}) {
  const projVars   = (variations || []).filter((i) => i.projectId === pid);
  const projClaims = (claims || []).filter((i) => i.projectId === pid);
  const projCerts  = (certificates || []).filter((i) => i.projectId === pid);
  const approvedVars = projVars.filter(isApprovedVariation);

  const hasSoq = Array.isArray(soqItems) && soqItems.some((i) => i.projectId === pid);

  const engineersEstimateTotalComputed = hasSoq
    ? computeEngineersEstimateTotal(soqItems, pid)
    : numberOrZero(engineersEstimateTotal);

  // Tender baseline: live SOQ first, then the BOQ tendered total prop, then a
  // direct reduce over boqRows (mobile doesn't receive the precomputed prop).
  const tenderedBaseline = (() => {
    if (hasSoq) {
      const t = computeTenderedTotal(soqItems, pid);
      if (t > 0) return t;
    }
    const propTotal = numberOrZero(boqTenderedTotal);
    if (propTotal > 0) return propTotal;
    return (boqRows || [])
      .filter((r) => !r.projectId || r.projectId === pid)
      .reduce((s, r) => s + numberOrZero(r.tenderedAmount), 0);
  })();

  // Certified to date = latest cumulative certificate's subtotal A.
  const sortedCerts = [...projCerts].sort((a, b) => {
    const d = String(a.certificateDate || "").localeCompare(String(b.certificateDate || ""));
    if (d !== 0) return d;
    return String(a.certificateNo || "").localeCompare(
      String(b.certificateNo || ""), undefined, { numeric: true, sensitivity: "base" }
    );
  });
  const latestCert = sortedCerts.length ? sortedCerts[sortedCerts.length - 1] : null;
  const certifiedToDate = numberOrZero(latestCert?.subtotalA);

  const pendingVoExp = projVars
    .filter((i) => !["Approved", "Rejected"].includes(String(i.status || "")))
    .reduce((s, i) => s + numberOrZero(i.assessedValue || i.quotedValue || i.estimatedValue || i.netVoAmount), 0);

  const pendingClaimExp = projClaims
    .filter((i) => String(i.status || "") === "In Review")
    .reduce((s, i) => s + numberOrZero(i.pgClaimed), 0);

  const awardedClaims = projClaims
    .filter((i) => String(i.status || "") === "Awarded")
    .reduce((s, i) => s + numberOrZero(i.pgAwarded), 0);

  // Approved VO additions — gross (additional work + existing-rate work).
  const voAdds = approvedVars
    .reduce((s, i) => s + numberOrZero(i.additionalWorkAmount) + numberOrZero(i.existingRatesAmount), 0);

  // Approved VO omissions — live from omissionItems (single source of truth).
  const voOmit = approvedVars
    .flatMap((v) => (Array.isArray(v.omissionItems) ? v.omissionItems : []))
    .reduce((s, e) => s + numberOrZero(e.omittedAmount), 0);

  const voOmissionItemNos = new Set();
  approvedVars.forEach((v) => {
    (v.omissionItems || []).forEach((e) => { if (e.itemNo) voOmissionItemNos.add(normItemNo(e.itemNo)); });
  });

  // SOQ savings via zero-final-qty (excludes items already in an approved VO omission).
  const soqZeroQtySavings = (soqItems || [])
    .filter((i) => i.projectId === pid)
    .filter((i) => {
      const feq = i.finalEstimatedQty;
      const isZeroed = feq === 0 || feq === "0" || feq === "" || feq === null || feq === undefined;
      const alreadyInVo = voOmissionItemNos.has(normItemNo(i.itemNo));
      return isZeroed && numberOrZero(i.tenderQty) > 0 && !alreadyInVo;
    })
    .reduce((s, i) => s + numberOrZero(i.tenderQty) * numberOrZero(i.tenderRate), 0);

  const totalDeductions = voOmit + soqZeroQtySavings;
  const rcv = tenderedBaseline - voOmit + voAdds - soqZeroQtySavings;
  const ffc = rcv + pendingClaimExp + pendingVoExp;
  const rem = rcv - certifiedToDate;
  const spPct = rcv > 0 ? (certifiedToDate / rcv) * 100 : 0;

  return {
    tenderedBaseline,
    engineersEstimateTotalComputed,
    latestCert,
    certifiedToDate,
    pendingVoExp,
    pendingClaimExp,
    awardedClaims,
    voAdds,
    voOmit,
    voOmissionItemNos,
    soqZeroQtySavings,
    totalDeductions,
    rcv,
    ffc,
    rem,
    spPct,
    counts: {
      varsTotal:      projVars.length,
      varsApproved:   approvedVars.length,
      varsPending:    projVars.filter((v) => !["Approved", "Rejected"].includes(String(v.status || ""))).length,
      varsRejected:   projVars.filter((v) => String(v.status || "") === "Rejected").length,
      claimsTotal:    projClaims.length,
      claimsInReview: projClaims.filter((c) => String(c.status || "") === "In Review").length,
      claimsAwarded:  projClaims.filter((c) => String(c.status || "") === "Awarded").length,
      daysAwarded:    projClaims.reduce((s, c) => s + numberOrZero(c.daysAwarded), 0),
    },
  };
}
